import type { Logger } from '@ricoh/ria.client.core/plugins/app/logger';
import type { DatabaseStorage } from '@ricoh/ria.client.core/plugins/storage/database';
import type { AxiosInstance, AxiosRequestConfig } from 'axios';

import { APP_INFO } from '@ricoh/ria.client.core/constants/core/app';
import { createLogger, loggerStyles } from '@ricoh/ria.client.core/plugins/app/logger';
import createDatabaseStorage from '@ricoh/ria.client.core/plugins/storage/database';
import { newStaticId } from '@ricoh/ria.client.core/utils/data/key';
import axios from 'axios';

import type { AuthConfig, AxiosPluginConfig, CSRFToken } from './_axios.type';

/** Axios Plugin. */
export default class AxiosPlugin {
  readonly #CSRF_CONFIG_ID = newStaticId(APP_INFO.CORE.ID, 'csrf');

  readonly #logger: Logger;

  readonly #config: AxiosPluginConfig;

  readonly #storage: DatabaseStorage;

  /**
   * @class AxiosPlugin
   * @param {AxiosPluginConfig} config Axios Plugin Config.
   */
  public constructor(config: AxiosPluginConfig) {
    this.#logger = createLogger('axios');
    this.#config = config;
    this.#storage = createDatabaseStorage(APP_INFO.CORE.DATABASE.NAME, APP_INFO.CORE.DATABASE.VERSION, (database) => {
      Object.values(APP_INFO.CORE.DATABASE.STORE.AUTH).forEach((store) => {
        if (!database.objectStoreNames.contains(store)) {
          database.createObjectStore(store, { keyPath: 'id' });
        }
      });
    });
  }

  /** Initialize Axios Plugin. */
  public async init() {
    this.#logger.write('info', `${loggerStyles.requestText('Init')} axios`);

    await this.#refreshCSRFToken();

    // * Clear CSRF Token early
    setInterval(async () => {
      await this.clearCSRFToken();
    }, this.#config.sessionDuration / 2);

    this.#logger.write('info', `${loggerStyles.responseText('Done')} init axios`);
  }

  /**
   * Create Axios Client.
   *
   * @param {AxiosRequestConfig} options Client options.
   *
   * @returns {AxiosInstance} Axios Client.
   */
  public createClient(options: AxiosRequestConfig): AxiosInstance {
    const client = axios.create(options);
    client.interceptors.request.use(async (config) => {
      using authConfig = await this.#authConfig();

      const csrfConfig = await authConfig.read(this.#CSRF_CONFIG_ID);

      // * Use CSRF Config
      if (csrfConfig) {
        const csrf = csrfConfig.value as CSRFToken;
        config.headers[csrf.headerName] = csrf.token;
      }
      // * Refresh CSRF Token
      else {
        const csrf = await this.#refreshCSRFToken();
        config.headers[csrf.headerName] = csrf.token;
      }

      return config;
    });

    return client;
  }

  /** Clear CSRF Token. */
  public async clearCSRFToken() {
    using authConfig = await this.#authConfig();

    await authConfig.delete(this.#CSRF_CONFIG_ID);
    this.#logger.write('info', `${loggerStyles.responseText('Clear')} CSRF token`);
  }

  async #refreshCSRFToken(): Promise<CSRFToken> {
    using authConfig = await this.#authConfig();

    const csrf = await this.#config.fetchCSRFToken();

    // * Create or Update CSRF Config
    const csrfConfig = await authConfig.read(this.#CSRF_CONFIG_ID);
    if (csrfConfig) {
      await authConfig.update({
        id: this.#CSRF_CONFIG_ID,
        value: csrf,
      });
    } else {
      await authConfig.create({
        id: this.#CSRF_CONFIG_ID,
        value: csrf,
      });
    }

    this.#logger.writeWithData('info', `${loggerStyles.responseText('Refresh')} CSRF token`, csrf);

    return csrf;
  }

  #authConfig() {
    return this.#storage.connect<AuthConfig>(APP_INFO.CORE.DATABASE.STORE.AUTH.CONFIGS);
  }
}
